import { Printer, ArrowLeft, Phone, Mail, MessageCircle, Linkedin, MapPin, Check, Download, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import {
  CONTACT, ECOSYSTEM, INFRA_SERVICES, TECH_SERVICES, INDUSTRIES,
  EXECUTION, WHY, MATERIAL_HANDLING, VALUES, GREENFIELD, BROWNFIELD,
  COMPANY_PROFILE_PDF,
} from "@/data/site";

const INFRA_LOGO = "/logos/automex-infra-stacked.png";
const TECH_LOGO = "/logos/automex-tech.png";

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2 text-[13px] leading-snug text-automex-navy">
      <Check className="mt-0.5 h-3.5 w-3.5 text-automex-teal shrink-0" />
      <span>{children}</span>
    </li>
  );
}

function SheetHead({ index, title, kicker }) {
  return (
    <div className="mb-6 border-b-2 border-automex-navy pb-3">
      <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-automex-teal">{kicker}</div>
      <h2 className="mt-1 flex items-baseline gap-3 font-heading text-3xl font-black uppercase tracking-tight text-automex-navy">
        <span className="text-automex-teal">{index}</span>{title}
      </h2>
    </div>
  );
}

export default function CompanyProfile() {
  useSeo({
    title: "Company Profile | AutoMex Group — Industrial Infrastructure & Technology",
    description: "AutoMex Group company profile: industrial infrastructure, turnkey project execution, PEB, industrial machinery, automation and material handling for modern manufacturing in Pune, India.",
    path: "/company-profile",
  });

  return (
    <div className="bg-slate-200 pt-20 print:pt-0 print:bg-white">
      {/* Screen-only action bar */}
      <div className="no-print sticky top-20 z-30 mx-auto flex max-w-[820px] flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-automex-navy hover:text-automex-blue" data-testid="profile-back">
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          <a
            href={COMPANY_PROFILE_PDF.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="profile-view-pdf"
            className="inline-flex items-center gap-2 rounded-sm border-2 border-automex-navy bg-white px-4 py-3 text-xs font-bold uppercase tracking-wider text-automex-navy transition-colors hover:bg-automex-navy hover:text-white"
          >
            <FileText className="h-4 w-4" /> View PDF
          </a>
          <a
            href={COMPANY_PROFILE_PDF.url}
            download={COMPANY_PROFILE_PDF.filename}
            data-testid="profile-download-pdf"
            className="inline-flex items-center gap-2 rounded-sm bg-automex-teal px-4 py-3 text-xs font-bold uppercase tracking-wider text-automex-navy transition-colors hover:bg-[#12b494]"
          >
            <Download className="h-4 w-4" /> Download PDF
          </a>
          <button
            onClick={() => window.print()}
            data-testid="profile-print"
            className="group inline-flex items-center gap-2 rounded-sm bg-automex-navy px-4 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-automex-ink"
          >
            <Printer className="h-4 w-4" /> Print Web Profile
          </button>
        </div>
      </div>

      <div className="print-doc mx-auto mb-16 max-w-[820px] bg-white shadow-xl print:mb-0 print:max-w-none print:shadow-none">
        {/* ---------- COVER ---------- */}
        <section className="print-sheet relative flex min-h-[1040px] flex-col justify-between overflow-hidden bg-automex-navy p-14 text-white print:min-h-screen">
          <div className="absolute inset-0 bg-blueprint-dark opacity-60" aria-hidden="true" />
          <div className="relative">
            <div className="inline-flex items-center gap-3">
              <svg width="40" height="40" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                <path d="M4 44 L24 8 L44 44 L33.5 44 L24 24.5 L14.5 44 Z" fill="#16C6A3" />
                <path d="M24 8 L31.5 21.5 L16.5 21.5 Z" fill="#ffffff" />
              </svg>
              <div className="font-heading text-2xl font-extrabold tracking-tight">
                AUTOMEX <span className="text-automex-teal">GROUP</span>
              </div>
            </div>
            <div className="mt-2 text-xs font-semibold uppercase tracking-[0.3em] text-automex-teal">
              Industrial Infrastructure &amp; Technology
            </div>
          </div>

          <div className="relative">
            <div className="text-sm font-bold uppercase tracking-[0.3em] text-automex-teal">Company Profile</div>
            <h1 className="mt-4 font-heading text-6xl font-black uppercase leading-[0.9] tracking-tighter">
              Building Industry.<br />
              <span className="text-automex-teal">Enabling Technology.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-200">
              AutoMex delivers industrial infrastructure, project execution, machinery, material handling
              and technology solutions for modern manufacturing.
            </p>
          </div>

          <div className="relative flex items-end justify-between gap-6 border-t border-white/20 pt-6">
            <div className="text-xs uppercase tracking-[0.25em] text-automex-teal">
              <div className="font-heading text-lg font-extrabold tracking-tight text-white">AUTOMEX GROUP</div>
              Building Industry. Enabling Technology.
            </div>
            <div className="text-right text-xs text-slate-300">
              <div className="font-semibold text-white">{CONTACT.location}</div>
              <div>{CONTACT.phone}</div>
              <div>{CONTACT.email}</div>
            </div>
          </div>
        </section>

        {/* ---------- 01 WHO WE ARE ---------- */}
        <section className="print-sheet p-14">
          <SheetHead index="01" kicker="Who We Are" title="One Platform. Two Capabilities." />
          <p className="text-sm leading-relaxed text-slate-700">
            AutoMex is being developed as an integrated industrial platform that combines infrastructure
            execution with industrial machinery and technology. Two connected verticals — <strong className="text-automex-blue">AutoMex
            Infra Projects</strong> and <strong className="text-automex-blue">AutoMex Industrial Technologies</strong> — allow a single
            partner to plan, build, equip, automate and commission a manufacturing facility.
          </p>

          <div className="mt-8 rounded-sm border border-slate-200 bg-slate-50 p-6">
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-automex-teal">The AutoMex Ecosystem</div>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
              {ECOSYSTEM.map((s, i) => (
                <span key={s} className="flex items-center gap-3 font-heading text-lg font-bold uppercase tracking-tight text-automex-navy">
                  {s}{i < ECOSYSTEM.length - 1 && <span className="text-automex-teal">→</span>}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-6">
            <div>
              <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-automex-navy">Vision</h3>
              <p className="mt-2 text-[13px] leading-relaxed text-slate-700">
                To become a trusted industrial partner for companies building and modernising the next
                generation of manufacturing in India.
              </p>
            </div>
            <div>
              <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-automex-navy">Mission</h3>
              <p className="mt-2 text-[13px] leading-relaxed text-slate-700">
                To simplify industrial development through engineering coordination, reliable execution,
                industrial technology and integrated solutions.
              </p>
            </div>
          </div>

          <div className="mt-8">
            <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-automex-navy">Values</h3>
            <div className="mt-3 grid grid-cols-3 gap-3">
              {VALUES.map((v, i) => (
                <div key={v} className="flex items-center gap-2 rounded-sm border border-slate-200 px-3 py-2">
                  <span className="font-heading text-sm font-black text-automex-teal">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-[12px] font-semibold uppercase tracking-tight text-automex-navy">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---------- 02 INFRA PROJECTS ---------- */}
        <section className="print-sheet p-14">
          <SheetHead index="02" kicker="Vertical One" title="AutoMex Infra Projects" />
          <div className="mb-6 rounded-sm bg-white border border-slate-200 p-4">
            <img src={INFRA_LOGO} alt="AutoMex Infra Projects" className="h-10 w-auto object-contain" />
          </div>
          <p className="text-sm leading-relaxed text-slate-700">
            Coordinated industrial infrastructure and project execution for manufacturing facilities,
            expansions and industrial developments — from factory plan to production.
          </p>
          <ul className="mt-6 grid grid-cols-2 gap-x-8 gap-y-2.5">
            {INFRA_SERVICES.map((s) => <Bullet key={s.title}>{s.title}</Bullet>)}
          </ul>

          <div className="mt-8 grid grid-cols-2 gap-6">
            <div className="rounded-sm border border-slate-200 p-5">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-automex-teal">Greenfield</div>
              <div className="font-heading text-base font-bold uppercase text-automex-navy">Built for New Factories</div>
              <ul className="mt-3 space-y-1.5">{GREENFIELD.map((g) => <Bullet key={g}>{g}</Bullet>)}</ul>
            </div>
            <div className="rounded-sm border border-slate-200 p-5">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-automex-teal">Brownfield</div>
              <div className="font-heading text-base font-bold uppercase text-automex-navy">Ready for Existing Ones</div>
              <ul className="mt-3 space-y-1.5">{BROWNFIELD.map((b) => <Bullet key={b}>{b}</Bullet>)}</ul>
            </div>
          </div>
        </section>

        {/* ---------- 03 INDUSTRIAL TECHNOLOGIES ---------- */}
        <section className="print-sheet p-14">
          <SheetHead index="03" kicker="Vertical Two" title="AutoMex Industrial Technologies" />
          <div className="mb-6 rounded-sm bg-white border border-slate-200 p-4">
            <img src={TECH_LOGO} alt="AutoMex Industrial Technologies" className="h-16 w-auto object-contain" />
          </div>
          <p className="text-sm leading-relaxed text-slate-700">
            Machinery, automation, material handling and industrial equipment — integrated with your
            infrastructure and production goals to move industry forward.
          </p>
          <ul className="mt-6 grid grid-cols-2 gap-x-8 gap-y-2.5">
            {TECH_SERVICES.map((s) => <Bullet key={s.title}>{s.title}</Bullet>)}
          </ul>

          <div className="mt-8 rounded-sm border border-slate-200 p-5">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-automex-teal">Material Handling</div>
            <div className="font-heading text-base font-bold uppercase text-automex-navy">Move Material. Move Production.</div>
            <ul className="mt-3 grid grid-cols-2 gap-x-8 gap-y-1.5">
              {MATERIAL_HANDLING.map((m) => <Bullet key={m}>{m}</Bullet>)}
            </ul>
            <p className="mt-3 text-[11px] text-slate-500">
              Delivered in association with <span className="font-semibold text-automex-blue">LiftoMech</span> — the associated
              material-handling &amp; crane brand — and integrated with AutoMex infrastructure projects.
            </p>
          </div>
        </section>

        {/* ---------- 04 HOW WE DELIVER ---------- */}
        <section className="print-sheet p-14">
          <SheetHead index="04" kicker="How We Deliver" title="Engineering-Led Execution" />
          <div className="grid grid-cols-3 gap-4">
            {EXECUTION.map((step) => (
              <div key={step.no} className="rounded-sm border border-slate-200 p-4">
                <div className="font-heading text-2xl font-black text-automex-teal">{step.no}</div>
                <div className="mt-1 font-heading text-sm font-bold uppercase tracking-tight text-automex-navy">{step.title}</div>
                <p className="mt-1 text-[12px] leading-snug text-slate-600">{step.desc}</p>
              </div>
            ))}
          </div>

          <h3 className="mt-8 font-heading text-lg font-bold uppercase tracking-tight text-automex-navy">Why AutoMex</h3>
          <div className="mt-3 grid grid-cols-2 gap-x-8 gap-y-3">
            {WHY.map((w) => (
              <div key={w.title}>
                <div className="flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-tight text-automex-navy">
                  <span className="h-1.5 w-1.5 bg-automex-teal" />{w.title}
                </div>
                <p className="ml-3.5 mt-0.5 text-[12px] leading-snug text-slate-600">{w.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ---------- 05 INDUSTRIES + CONTACT ---------- */}
        <section className="print-sheet p-14">
          <SheetHead index="05" kicker="Reach & Contact" title="Industries We Serve" />
          <div className="grid grid-cols-4 gap-2">
            {INDUSTRIES.map((ind) => (
              <div key={ind.name} className="rounded-sm border border-slate-200 px-3 py-3 text-center text-[12px] font-semibold uppercase tracking-tight text-automex-navy">
                {ind.name}
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-sm bg-automex-navy p-8 text-white">
            <div className="text-sm font-bold uppercase tracking-[0.25em] text-automex-teal">Discuss Your Project</div>
            <h3 className="mt-2 font-heading text-3xl font-black uppercase tracking-tight">
              Planning Your Next Industrial Project?
            </h3>
            <p className="mt-2 text-sm text-slate-200">Tell us what you are building, expanding or automating.</p>
            <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-3"><Phone className="h-4 w-4 text-automex-teal" />{CONTACT.phone}</div>
              <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-automex-teal" />{CONTACT.email}</div>
              <div className="flex items-center gap-3"><MessageCircle className="h-4 w-4 text-automex-teal" />WhatsApp {CONTACT.phone}</div>
              <div className="flex items-center gap-3"><MapPin className="h-4 w-4 text-automex-teal" />{CONTACT.location}</div>
              <div className="flex items-center gap-3"><Linkedin className="h-4 w-4 text-automex-teal" />AutoMex Infra Projects</div>
              <div className="flex items-center gap-3"><Linkedin className="h-4 w-4 text-automex-teal" />AutoMex Industrial Technologies</div>
            </div>
          </div>

          <div className="mt-8 text-center text-[11px] uppercase tracking-[0.3em] text-slate-400">
            AutoMex Group · Building Industry. Enabling Technology.
          </div>
        </section>
      </div>
    </div>
  );
}
